
import { useState, useEffect } from 'react';
import { Sparkles, Star, Rotate3d } from 'lucide-react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  speed: number;
  opacity: number;
  type: 'sparkle' | 'star' | 'cube';
}

const AnimatedBackground = () => {
  const [particles, setParticles] = useState<Particle[]>([]);
  
  useEffect(() => {
    // Create initial particles
    const initialParticles: Particle[] = [];
    const types: ('sparkle' | 'star' | 'cube')[] = ['sparkle', 'star', 'cube'];
    
    for (let i = 0; i < 20; i++) {
      initialParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 1.5 + 0.5,
        speed: Math.random() * 1 + 0.5,
        opacity: Math.random() * 0.6 + 0.2,
        type: types[Math.floor(Math.random() * types.length)]
      });
    }
    
    setParticles(initialParticles);
    
    // Animate particles
    const animationInterval = setInterval(() => {
      setParticles(prevParticles => 
        prevParticles.map(particle => ({
          ...particle,
          y: (particle.y + particle.speed * 0.1) % 100,
          opacity: Math.sin(Date.now() / 2000 + particle.id) * 0.3 + 0.4
        }))
      );
    }, 50);
    
    return () => clearInterval(animationInterval);
  }, []);

  const renderParticleIcon = (type: 'sparkle' | 'star' | 'cube') => {
    switch (type) {
      case 'sparkle':
        return <Sparkles className="text-accent" />;
      case 'star':
        return <Star className="text-secondary" />;
      case 'cube':
        return <Rotate3d className="text-primary" />;
    }
  };
  
  return (
    <div className="fixed inset-0 -z-10 pointer-events-none">
      <div className="absolute inset-0 bg-gradient-radial from-background via-background to-muted opacity-80"></div>
      
      {/* Moving particles */}
      {particles.map(particle => (
        <div 
          key={particle.id}
          className="absolute animate-float transition-all duration-1000"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            transform: `scale(${particle.size})`,
            opacity: particle.opacity,
            transition: 'opacity 1s ease-in-out'
          }}
        >
          {renderParticleIcon(particle.type)}
        </div>
      ))}
      
      {/* Gradient orbs */}
      <div className="absolute top-1/3 left-1/5 w-64 h-64 rounded-full bg-primary/10 filter blur-3xl animate-float"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-secondary/10 filter blur-3xl animate-float-slow"></div>
      <div className="absolute top-2/3 right-1/3 w-48 h-48 rounded-full bg-accent/10 filter blur-2xl animate-pulse-slow"></div>
    </div>
  );
};

export default AnimatedBackground;
